import type { PureFunction } from '@bonsai-components/utility-types';
import type {
  RepositoryFragment,
  RepositoryIdentityFragment,
  RepositoryInput
} from '../__generated__/apollo-hooks';
import {
  ACCOUNT_SETTINGS_PATH,
  CHANGELOG_PATH,
  CODE_SEARCH_PATH,
  DEVCENTER_PATH,
  MARKETPLACE_RECIPES_PATH,
  NEW_BUILDER_PATH,
  RECENT_ACTIVITY_PATH,
  SUPPORT_EMAIL
} from '../constants/general';
import {
  type DefaultParam,
  encodeJSONBase64,
  encodeRecipeDefaults,
  toBase64
} from './encoding.helper';
import { GLOBAL_ORGANIZATION_ID } from './organizations.helper';

type RecipeDetailsUrlProps = {
  id: string;
  defaultOptions?: DefaultParam;
  selectedRepository?: RepositoryInput | RepositoryFragment;
  organizationId?: string;
  categoryId?: string;
};
/**
 *
 * @param {string} id  Recipe ID Example: `org.openrewrite.recipe.someJavaFoo`
 * @returns {string} Example: `/recipes/org.openrewrite.recipe.someJavaFoo`
 */
export const recipeDetailsUrl: PureFunction<RecipeDetailsUrlProps, string> = ({
  id,
  defaultOptions,
  selectedRepository,
  organizationId,
  categoryId
}): string => {
  const recipeParams = new URLSearchParams();

  if (organizationId) {
    recipeParams.set('organizationId', toBase64(organizationId));
  }

  if (selectedRepository) {
    recipeParams.set('repository', encodeJSONBase64(selectedRepository));
  }

  if (categoryId) {
    recipeParams.set('category', categoryId);
  }

  let defaults = '';

  if (Array.isArray(defaultOptions) && defaultOptions.length > 0) {
    defaults = `#defaults=${encodeRecipeDefaults(defaultOptions)}`;
  }

  const hasEntries = [...recipeParams.entries()].length > 0;

  return `/recipes/${id}${
    hasEntries ? `?${recipeParams.toString()}` : ''
  }${defaults}`;
};

type ReplayBuilderUrlProps = {
  recipeRunId: string;
  organizationId?: string;
};

export const replayBuilderUrl: PureFunction<ReplayBuilderUrlProps, string> = ({
  recipeRunId,
  organizationId
}): string => {
  const recipeParams = new URLSearchParams();

  if (organizationId) {
    recipeParams.set('organizationId', toBase64(organizationId));
  }

  const hasEntries = [...recipeParams.entries()].length > 0;

  return `${NEW_BUILDER_PATH}/${recipeRunId}${
    hasEntries ? `?${recipeParams.toString()}` : ''
  }`;
};

export const recipeResultsWithRepositoryUrl: PureFunction<
  {
    recipeRunId?: string;
    repository: RepositoryIdentityFragment;
    organizationId?: string;
    params?: URLSearchParams;
  },
  string | undefined
> = ({ recipeRunId, repository, organizationId, params }) => {
  if (!organizationId || !recipeRunId) {
    return undefined;
  }

  const recipeParams = new URLSearchParams();
  recipeParams.set('repo', encodeURIComponent(encodeJSONBase64(repository)));
  if (params) {
    params.forEach((value, key) => {
      recipeParams.set(key, value);
    });
  }

  return recipeResultsUrl({
    recipeRunId,
    organizationId,
    queryParams: recipeParams.toString()
  });
};

type RecipeResultsUrlParams = {
  recipeRunId?: string;
  organizationId?: string;
  queryParams?: string;
};

/**
 *
 * @param {string} recipeRunId  Recipe ID Example: `ympPF`
 * @returns {string} Example: `/results/232/ympPF`
 */
export const recipeResultsUrl = (
  params: RecipeResultsUrlParams
): string | undefined => {
  const { recipeRunId, organizationId, queryParams } = params;

  if (!organizationId || !recipeRunId) {
    return undefined;
  }

  const urlParams = new URLSearchParams(queryParams);
  const queryString = urlParams.toString();

  return `/results/${encodeURIComponent(organizationId)}/${recipeRunId}${queryString ? `?${queryString}` : ''}`;
};

type RepositoryTableUrlParams = {
  recipeRunId?: string;
  organizationId?: string;
};

/**
 *
 * @param {string} recipeRunId  Recipe ID Example: `ympPF`
 * @returns {string} Example: `/results/232/ympPF/repositories`
 */
export const repositoryTableUrl = (
  params: RepositoryTableUrlParams
): string | undefined => {
  const { recipeRunId, organizationId } = params;

  if (!organizationId || !recipeRunId) {
    return undefined;
  }

  return `/results/${encodeURIComponent(organizationId)}/${recipeRunId}/repositories`;
};

type DataTablesUrlParams = {
  recipeRunId?: string;
  organizationId?: string;
};

export const DATA_TABLES_QUERY_PARAM = 'q';

export const dataTablesUrl = (
  params: DataTablesUrlParams & { dataTableName?: string }
): string | undefined => {
  const { recipeRunId, organizationId, dataTableName } = params;
  const resultsUrl = recipeResultsUrl({ recipeRunId, organizationId });

  if (!resultsUrl) {
    return undefined;
  }

  const basePath = `${resultsUrl}/data-tables`;

  if (dataTableName) {
    const urlParams = new URLSearchParams();
    urlParams.set(DATA_TABLES_QUERY_PARAM, dataTableName);
    return `${basePath}?${urlParams.toString()}`;
  }

  return basePath;
};

export const agentTeamUrl = (
  params: DataTablesUrlParams
): string | undefined => {
  const { recipeRunId, organizationId } = params;

  if (!organizationId || !recipeRunId) {
    return undefined;
  }

  return `/results/${encodeURIComponent(organizationId)}/${recipeRunId}/team`;
};

/**
 *
 * @param {string} query Example: `org.openrewrite`
 * @returns {string} Example: `/catalog/org.openrewrite
 */
export const recipeCategoryUrl: PureFunction<{ id: string }, string> = ({
  id
}): string => `${MARKETPLACE_RECIPES_PATH}/${encodeURIComponent(id)}`;

type CommitResultsUrlParams = {
  commitId: string;
  organizationId: string;
  changesetId: string;
};

export const commitResultsUrl = (params: CommitResultsUrlParams): string => {
  const { commitId, organizationId, changesetId } = params;
  const urlParams = new URLSearchParams();
  urlParams.set('organizationId', organizationId);
  urlParams.set('changesetId', changesetId);
  return `/commits/${commitId}?${urlParams.toString()}`;
};

export const handleMiddleClickOpen = (url: string) => (event) =>
  event.button === 1 &&
  window.open(url, '_blank', 'noopener=true,noreferrer=true');

export const visualizationsForRecipeRunUrl: PureFunction<
  {
    recipeRunId?: string;
    organizationId?: string;
    fromVisualizationRun?: string;
  },
  string | undefined
> = ({ recipeRunId, organizationId, fromVisualizationRun }) => {
  if (!organizationId || !recipeRunId) {
    return undefined;
  }

  const basePath = `/results/${encodeURIComponent(organizationId)}/${recipeRunId}/visualizations`;

  if (fromVisualizationRun) {
    const urlParams = new URLSearchParams();
    urlParams.set('fromVisualizationRun', fromVisualizationRun);
    return `${basePath}?${urlParams.toString()}`;
  }

  return basePath;
};

export const visualizationRunUrl: PureFunction<
  {
    id: string;
    organizationId: string;
    recipeRunId: string;
  },
  string
> = ({ id, organizationId, recipeRunId }): string => {
  const urlParams = new URLSearchParams();
  urlParams.set('organizationId', organizationId);
  urlParams.set('recipeRunId', recipeRunId);
  return `/visualizations/${id}?${urlParams.toString()}`;
};

/**
 *
 * @param {string} query Example: `spring`
 * @returns {string} Example: `/search?q=spring`
 */
export const searchForRecipeUrl = (query: string): string =>
  `/search?q=${encodeURIComponent(query)}`;

/**
 * Helper function to build organization-based URLs
 */
const buildOrgUrl = (
  basePath: string,
  organizationId: string,
  queryParams?: string
): string => {
  if (
    !organizationId ||
    organizationId === 'null' ||
    organizationId === GLOBAL_ORGANIZATION_ID
  ) {
    return basePath;
  }

  const encodedOrgId = encodeURIComponent(organizationId);

  return `${basePath}/${encodedOrgId}${queryParams ? `?${queryParams}` : ''}`;
};

type DevCenterUrlParams = {
  organizationId: string;
  /**
   * @deprecated no longer required
   */
  v3?: boolean;
  recipeRunId?: string;
};

/**
 * Returns the URL for the DevCenter based on the organization ID.
 * If the organization ID is not provided or is 'null', it returns the default DevCenter path.
 *
 * @param {string} params.organizationId - The organization ID.
 * @returns {string} - The URL for the DevCenter.
 */
export const devCenterUrl: PureFunction<DevCenterUrlParams, string> = ({
  organizationId,
  recipeRunId,
  v3
}) => {
  const basePath = DEVCENTER_PATH;
  const queryParams = recipeRunId ? `recipeRunId=${recipeRunId}` : undefined;

  return buildOrgUrl(basePath, organizationId, queryParams);
};

export const devCenterConfigureUrl = (organizationId: string) => {
  if (!organizationId || organizationId === GLOBAL_ORGANIZATION_ID) {
    return '/devcenter/configure';
  }
  return `/devcenter/configure?organizationId=${encodeURIComponent(organizationId)}`;
};

export const activityUrl = (organizationId: string) => {
  return buildOrgUrl(RECENT_ACTIVITY_PATH, organizationId);
};

export const changelogUrl = (organizationId: string) => {
  return buildOrgUrl(CHANGELOG_PATH, organizationId);
};

export const codeSearchUrl = (organizationId: string) => {
  return buildOrgUrl(CODE_SEARCH_PATH, organizationId);
};

/**
 * Determines the href value based on the provided input.
 * If the input is a function, it will be executed and its return value will be used as the href.
 * If the input is a string, it will be directly used as the href.
 *
 * @param href - The href value or a function that returns the href value.
 * @returns The determined href value.
 */
export const determineHref = (href: string | (() => string)): string => {
  if (typeof href === 'function') {
    return href();
  }
  return href;
};

/**
 * Opens the specified URL in a new browser tab.
 *
 * @param url - The URL to open.
 */
export const openInNewTab = (url: string) =>
  window.open(url, '_blank', 'noopener=true,noreferrer=true');

export const isRightOrMiddleClick = (e): boolean =>
  e.button >= 1 || (e.button === 0 && (e.ctrlKey || e.metaKey));

const browserLocation = (): Location | undefined =>
  typeof window === 'undefined' ? undefined : window.location;

/**
 * A service beside the UI, named by a prefix before the UI's own host: `status.app.moderne.io` beside
 * app.moderne.io, as on every SaaS tenant, and `status.moderne.localhost:8443` beside a self-hosted deployment.
 */
export const siblingServiceUrl = (
  prefix: string,
  location: Pick<Location, 'protocol' | 'host'> | undefined = browserLocation()
): string =>
  location ? `${location.protocol}//${prefix}.${location.host}` : '';

export const createSupportEmailUrl = ({ subject, body }) => {
  return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${encodeURI(body)}`;
};

type AccountSettingsTabs =
  | 'connection'
  | 'integrations'
  | 'access-tokens'
  | 'appearance';
export const accountSettingsUrl = (tab: AccountSettingsTabs) =>
  `${ACCOUNT_SETTINGS_PATH}/${tab}`;
